import React, { useCallback, useEffect, useRef, useState } from "react";
import { useTabParam } from "../hooks/useTabParam";
import PageHeader from "../components/PageHeader";
import { Box, Paper, Tab, Tabs, Typography } from "@mui/material";
import RefreshIcon from "@mui/icons-material/Refresh";
import { COLORS } from "../assets/theme";
import { useUser } from "../contexts/UserContext";
import { getAllBusinesses } from "../services/VenueService";
import {
  listMatches,
  listMatchLevels,
  Match,
  MATCH_STATUSES,
  MatchLevel,
  MatchLevelsStatus,
  MatchListParams,
  MatchMode,
  MatchStatus,
  MatchWhen,
} from "../services/MatchmakingService";
import { hasPermission, PERMISSIONS } from "../utils/permissions";
import MatchDetailsDrawer from "../components/matchmaking/MatchDetailsDrawer";
import MatchLevelsPanel from "../components/matchmaking/MatchLevelsPanel";
import {
  BookingStateBadge,
  courtLabel,
  formatTime,
  ModeBadge,
  StatusBadge,
  titleCase,
} from "../components/matchmaking/matchmakingUi";
import { formatTableDate } from "../utils/tableDateFormat";
import Actions from "../components/Actions";
import CustomTable from "../components/table/CustomTable";
import SearchHeaderContainer from "../components/table/SearchHeaderContainer";
import { IField, IPageable, Params } from "../types/table/interfaces.type";

const DEFAULT_PAGE_SIZE = 20;
const ALL = "ALL";

type BusinessOption = { id: string; name: string };

const STATUS_OPTIONS = MATCH_STATUSES.map((status) => ({
  label: titleCase(status),
  value: status,
}));

const MODE_OPTIONS = [
  { label: "All", value: ALL },
  { label: "Public", value: "PUBLIC" },
  { label: "Private", value: "PRIVATE" },
];

const WHEN_OPTIONS = [
  { label: "All dates", value: "all" },
  { label: "Upcoming", value: "upcoming" },
  { label: "Past", value: "past" },
];

const tabSx = {
  textTransform: "none",
  fontSize: { xs: "0.8rem", sm: "0.9rem" },
  "&:hover": { bgcolor: "transparent" },
} as const;

const mutedSx = { fontSize: "0.76rem", color: COLORS.placeHolderColor };

const MATCH_FIELDS: IField<Match>[] = [
  {
    key: "appointment",
    header: "Date / time",
    render: (match) => (
      <>
        <Typography sx={{ fontSize: "0.88rem", fontWeight: 600 }}>
          {formatTableDate(match.appointment?.start_at)}
        </Typography>
        <Typography sx={mutedSx}>
          {formatTime(match.appointment?.start_at)} –{" "}
          {formatTime(match.appointment?.end_at)}
        </Typography>
      </>
    ),
  },
  { key: "appointment_id", header: "Venue · court", render: courtLabel },
  {
    key: "owner",
    header: "Host",
    render: (match) => (
      <>
        <Typography sx={{ fontSize: "0.88rem" }}>
          {match.owner?.full_name || "-"}
        </Typography>
        <Typography sx={mutedSx}>{match.owner?.phone || ""}</Typography>
      </>
    ),
  },
  { key: "level", header: "Level", render: (match) => match.level || "—" },
  { key: "mode", header: "Visibility", render: (match) => <ModeBadge mode={match.mode} /> },
  { key: "status", header: "Status", render: (match) => <StatusBadge status={match.status} /> },
  {
    key: "currentPlayers",
    header: "Players",
    render: (match) => `${match.currentPlayers}/${match.targetPlayers}`,
  },
  {
    key: "pendingRequestsCount",
    header: "Pending",
    render: (match) => (
      <Typography
        component="span"
        sx={{
          fontSize: "inherit",
          fontWeight: match.pendingRequestsCount > 0 ? 700 : 400,
          color: match.pendingRequestsCount > 0 ? COLORS.ORANGE : COLORS.placeHolderColor,
        }}
      >
        {match.pendingRequestsCount}
      </Typography>
    ),
  },
  { key: "bookingPaid", header: "Booking", render: (match) => <BookingStateBadge match={match} /> },
];

const MatchmakingContent: React.FC = () => {
  const { tab, setTab } = useTabParam(["matches", "levels"]);

  const [statuses, setStatuses] = useState<MatchStatus[]>([]);
  const [mode, setMode] = useState<MatchMode | typeof ALL>(ALL);
  const [when, setWhen] = useState<MatchWhen>("all");
  const [businessId, setBusinessId] = useState(ALL);
  const [search, setSearch] = useState("");
  // Bumped to refetch the current page without touching the filters.
  const [refreshKey, setRefreshKey] = useState(0);
  const refresh = () => setRefreshKey((key) => key + 1);

  // The table calls fetchFunc on every render; only start a request when the
  // query changes, and let a superseded request settle with the newest one so
  // a slow response can't overwrite fresher rows.
  const requestRef = useRef<{ key: string; promise: Promise<IPageable<Match>> } | null>(null);
  const rowsRef = useRef<Match[]>([]);

  const [businesses, setBusinesses] = useState<BusinessOption[]>([]);
  const [levels, setLevels] = useState<MatchLevel[]>([]);
  const [levelsStatus, setLevelsStatus] = useState<MatchLevelsStatus>("loading");
  const [selected, setSelected] = useState<Match | null>(null);

  useEffect(() => {
    getAllBusinesses()
      .then((list: any[]) =>
        setBusinesses(
          (list || [])
            .map((business) => ({
              id: String(business?.id || ""),
              name: String(business?.name || business?.short_name || "").trim(),
            }))
            .filter((business) => business.id)
        )
      )
      .catch(() => setBusinesses([]));
  }, []);

  const loadLevels = useCallback(async () => {
    setLevelsStatus("loading");
    try {
      setLevels(await listMatchLevels());
      setLevelsStatus("loaded");
    } catch {
      // The interceptor already surfaced the message.
      setLevelsStatus("failed");
    }
  }, []);

  useEffect(() => {
    loadLevels();
  }, [loadLevels]);

  // Every levels mutation answers with the full list, so a success also means
  // the list is known again.
  const handleLevelsChange = (next: MatchLevel[]) => {
    setLevels(next);
    setLevelsStatus("loaded");
  };

  const filterKey = JSON.stringify([statuses, mode, when, businessId, search.trim()]);

  const fetchMatches = useCallback(
    (params: Params<Match>): Promise<IPageable<Match>> => {
      const query: MatchListParams = {
        status: statuses,
        mode: mode === ALL ? undefined : mode,
        when,
        businessId: businessId === ALL ? undefined : businessId,
        search: search.trim() || undefined,
        page: params.page ?? 0,
        size: params.size ?? DEFAULT_PAGE_SIZE,
      };
      const key = JSON.stringify([query, refreshKey]);
      if (requestRef.current?.key === key) return requestRef.current.promise;

      const promise: Promise<IPageable<Match>> = listMatches(query).then((result) => {
        if (requestRef.current?.key !== key) return requestRef.current!.promise;
        rowsRef.current = result.items;
        return {
          content: result.items,
          number: query.page!,
          size: query.size!,
          totalElements: result.total,
          totalPages: Math.max(1, Math.ceil(result.total / query.size!)),
        };
      });
      requestRef.current = { key, promise };
      return promise;
    },
    [statuses, mode, when, businessId, search, refreshKey]
  );

  const viewActions = Actions({
    onView: {
      handler: (id) => {
        const match = rowsRef.current.find((row) => row.id === id);
        if (match) setSelected(match);
      },
    },
  });

  const replaceRow = (updated: Match) => {
    const { requests, ...row } = updated as Match & { requests?: unknown };
    setSelected((prev) => (prev && prev.id === row.id ? row : prev));
    refresh();
  };

  const removeRow = () => {
    setSelected(null);
    refresh();
  };

  return (
    <Box sx={{ py: 2 }}>
      <PageHeader
        title="Matchmaking"
        subtitle="Every match across your venues, and the levels players can pick from."
        sx={{ mb: 1.5 }}
      />

      <Tabs
        value={tab}
        onChange={(_, next) => {
          setTab(next);
          // A rename on the Levels tab rewrites the level on existing matches.
          if (next === 0) refresh();
        }}
        sx={{ borderBottom: 1, borderColor: "divider", mb: 2 }}
      >
        <Tab label="Matches" sx={tabSx} />
        <Tab label="Levels" sx={tabSx} />
      </Tabs>

      {tab === 1 && (
        <MatchLevelsPanel
          levels={levels}
          status={levelsStatus}
          onChange={handleLevelsChange}
          onRetry={loadLevels}
        />
      )}

      {tab === 0 && (
        <>
          <SearchHeaderContainer
            search={search}
            setSearch={setSearch}
            selectConfig={{
              label: "Status",
              placeholder: "All statuses",
              options: STATUS_OPTIONS,
              value: statuses,
              size: "small",
              multiSelect: true,
              clearable: true,
              onChange: (value: any) =>
                setStatuses(Array.isArray(value) ? (value as MatchStatus[]) : []),
            }}
            selectConfig2={{
              label: "Visibility",
              options: MODE_OPTIONS,
              value: mode,
              size: "small",
              onChange: (value: any) => setMode((value || ALL) as MatchMode | typeof ALL),
            }}
            extraSelects={[
              {
                label: "Date",
                options: WHEN_OPTIONS,
                value: when,
                size: "small",
                onChange: (value: any) => setWhen((value || "all") as MatchWhen),
              },
              {
                label: "Venue",
                options: [
                  { label: "All venues", value: ALL },
                  ...businesses.map((business) => ({
                    label: business.name || business.id,
                    value: business.id,
                  })),
                ],
                value: businessId,
                size: "small",
                searchable: businesses.length > 8,
                onChange: (value: any) => setBusinessId(String(value || ALL)),
              },
            ]}
            buttonConfig={[
              {
                label: "Refresh",
                size: "small",
                variant: "outlined",
                startIcon: <RefreshIcon />,
                onClick: refresh,
                minHeight: 36,
                sx: { width: { xs: "100%", sm: "auto" } },
              },
            ]}
          />

          {/* Remount on a filter change so the table starts again from page one. */}
          <CustomTable<Match>
            key={filterKey}
            filters={{ page: 0, size: DEFAULT_PAGE_SIZE }}
            fields={MATCH_FIELDS}
            actions={viewActions}
            fetchFunc={fetchMatches}
          />
        </>
      )}

      <MatchDetailsDrawer
        match={selected}
        levels={levels}
        levelsStatus={levelsStatus}
        onClose={() => setSelected(null)}
        onUpdated={replaceRow}
        onDeleted={removeRow}
      />
    </Box>
  );
};

/** The backend 403s every call without the permission; say so instead of a broken page. */
const Matchmaking: React.FC = () => {
  const { user } = useUser();

  if (!hasPermission(PERMISSIONS.MANAGE_MATCHMAKING, user)) {
    return (
      <Box sx={{ display: "flex", justifyContent: "center", p: { xs: 2, sm: 4 } }}>
        <Paper
          elevation={0}
          sx={{
            maxWidth: 520,
            width: "100%",
            p: 3,
            borderRadius: 3,
            border: `1px solid ${COLORS.BorderColor}`,
          }}
        >
          <Typography sx={{ fontWeight: 700, mb: 1 }}>Matchmaking</Typography>
          <Typography sx={{ color: COLORS.placeHolderColor, fontSize: "0.9rem" }}>
            You don't have permission to manage matchmaking. Ask an administrator
            to grant you the Manage matchmaking permission.
          </Typography>
        </Paper>
      </Box>
    );
  }

  return <MatchmakingContent />;
};

export default Matchmaking;
